import { Dialog, Keys } from '@robotensor/ui'
import { Fragment, useEffect, useSyncExternalStore } from 'react'
import type { NavDef } from './nav.tsx'

export interface ShortcutRow {
  keys: string
  label: string
}

// Pages with their own keys (the recording workspaces) list them here while they're open; the dialog shows them
// first, and a page's "Keyboard" button opens the same dialog as `?`.
let pageGroups: readonly { title: string; rows: readonly ShortcutRow[] }[] = []
const listeners = new Set<() => void>()
const openers = new Set<() => void>()
const emit = () => listeners.forEach((l) => l())

/** Lists a page's shortcuts in the shortcuts dialog while the page is mounted. */
export function usePageShortcuts(title: string, rows: readonly ShortcutRow[]): void {
  useEffect(() => {
    const group = { title, rows }
    pageGroups = [...pageGroups, group]
    emit()
    return () => {
      pageGroups = pageGroups.filter((g) => g !== group)
      emit()
    }
  }, [title, rows])
}

/** Opens the shortcuts dialog of whichever frame is mounted (AppFrame or Shell). */
export function openShortcuts(): void {
  openers.forEach((o) => o())
}

/** Frames register how to open their dialog. */
export function useShortcutsOpener(open: () => void): void {
  useEffect(() => {
    openers.add(open)
    return () => {
      openers.delete(open)
    }
  }, [open])
}

function usePageGroups() {
  return useSyncExternalStore(
    (l) => {
      listeners.add(l)
      return () => listeners.delete(l)
    },
    () => pageGroups,
  )
}

const GENERAL: { keys: string; label: string }[] = [
  { keys: 'mod+k', label: 'Command palette: pages, actions, ids' },
  { keys: '?', label: 'This list' },
  { keys: '/', label: 'Focus search on the page' },
  { keys: '[', label: 'Collapse or expand the sidebar' },
]

const LISTS: { keys: string; label: string }[] = [
  { keys: 'j', label: 'Next row' },
  { keys: 'k', label: 'Previous row' },
  { keys: 'enter', label: 'Open the row' },
  { keys: 'x', label: 'Select the row (where selectable)' },
  { keys: 'escape', label: 'Clear selection or cursor' },
  { keys: 'shift+g', label: 'Last row' },
]

const TIME: { keys: string; label: string }[] = [
  { keys: 'arrowleft', label: 'Playhead back 1% (Shift: 10%)' },
  { keys: 'arrowright', label: 'Playhead forward' },
  { keys: 'l', label: 'Back to live (event stream)' },
]

export function ShortcutsDialog({ open, onOpenChange, nav }: { open: boolean; onOpenChange: (o: boolean) => void; nav: readonly NavDef[] }) {
  const go = nav.filter((n) => n.keys).map((n) => ({ keys: n.keys!, label: n.label }))
  const page = usePageGroups()
  return (
    <Dialog open={open} onOpenChange={onOpenChange} title="Keyboard shortcuts" description="Everything here works without a pointer." width={720}>
      <div className="c-shortcuts">
        {[
          ...page.map((g) => [g.title, g.rows] as const),
          ['General', GENERAL],
          ['Go to', go],
          ['Lists', LISTS],
          ['Timelines', TIME],
        ].map(([title, rows]) => (
          <section key={title as string}>
            <h3 className="rt-label">{title as string}</h3>
            <dl>
              {(rows as { keys: string; label: string }[]).map((r) => (
                <Fragment key={r.keys}>
                  <dt>{r.label}</dt>
                  <dd>
                    <Keys keys={r.keys} />
                  </dd>
                </Fragment>
              ))}
            </dl>
          </section>
        ))}
      </div>
    </Dialog>
  )
}
